// The system-supported declarations of the Internal Review (SEP-243 sections
// 28 and 29).
//
// Section 28 keeps the eight conclusion fields of the form as they are. Section
// 29 asks the platform to SUPPORT three of the six declarations with the data
// the firm already holds:
//
//   1. "Costs and charges disclosed" reads whether the Schedule of Charges is
//      recorded as provided on the register of section 4.
//   2. "Investment restrictions considered" shows the ACTUAL restrictions of
//      the fact find beside the confirmation.
//   3. "Risk warnings provided" reads the two risk documents of the sales
//      process on the same register.
//
// THE SYSTEM SUPPORTS THE DECISION OF THE IM. IT NEVER MAKES THE SUITABILITY
// JUDGEMENT. The fold below therefore states three different things, and it
// keeps them apart:
//
//   - `supported` — the firm holds data that speaks to this declaration.
//   - `satisfied` — that data records a FACT that settles the declaration.
//     `true` or `false` for a fact the register holds. `null` where the
//     declaration is a judgement that only a person makes, and where the data
//     is evidence and never a verdict.
//   - `evidence` and `outstanding` — the rows the reader must look at.
//
// "Disclosed" and "provided" are facts: the register records that the firm gave
// a document on a date. "Considered" is a judgement: no row can prove that an
// IM thought about a restriction, so that entry carries evidence and never a
// verdict.
//
// THE OTHER THREE STAY PLAIN TICK BOXES, AND THE FIRM CONFIRMED IT on
// 2026-08-30 (SEP-243 item 7). "All necessary information collected",
// "circumstances fully assessed" and "suitable strategy recommended" carry no
// automated validation, because the firm holds no record that speaks to any of
// them. This is a decision and not a gap: nobody should expect a check here.
//
// The fold is pure, and nothing stores its result. It lives in the wire layer,
// so the panel and the controller read one definition (ADR-0003).

import { z } from 'zod';

import { providedDocumentCatalogueItem } from './onboarding-document-registers';

/** The six declarations of section 7 of the form, in the order it prints them. */
export const SUITABILITY_DECLARATION_KEYS = [
    'informationCollected',
    'circumstancesAssessed',
    'strategyRecommended',
    'riskWarningsProvided',
    'investmentRestrictionsConsidered',
    'costChargesDisclosed',
] as const;

export type SuitabilityDeclarationKey = (typeof SUITABILITY_DECLARATION_KEYS)[number];

export const suitabilityDeclarationKeySchema = z.enum(SUITABILITY_DECLARATION_KEYS);

/**
 * The catalogue codes that speak to the "risk warnings provided" declaration.
 *
 * The Risk and Investment Guide and the Capacity for Loss Guide are the two
 * items of firm literature that carry the risk warnings of the sales process.
 * THE FIRM CONFIRMED THIS PAIR on 2026-08-30 (SEP-243 item 7). The reason is in
 * the documents themselves: the Risk and Investment Guide carries the
 * investment-risk material, and the Capacity for Loss Guide opens by telling
 * the client to "read this guide in conjunction with the Risk and Investment
 * Guide". Neither one stands alone, so the declaration reads both.
 *
 * EACH CODE MUST RESOLVE THROUGH `providedDocumentCatalogueItem`. A rename in
 * the catalogue that this list misses turns the declaration support off in
 * silence, so `assertDeclarationDocumentCodes` below fails the build for it,
 * and `suitability-declaration-support.test.ts` runs the assertion.
 */
export const RISK_WARNING_DOCUMENT_CODES: ReadonlyArray<string> = [
    'risk_and_investment_guide',
    'capacity_for_loss_guide',
];

/** The catalogue code that speaks to the "costs and charges" declaration. */
export const COSTS_AND_CHARGES_DOCUMENT_CODE = 'schedule_of_charges';

/**
 * Every code the two declarations read, in one list.
 *
 * The guard test walks it, and the fold below prints the label of each entry,
 * so a reader of the review never meets a raw catalogue code.
 */
export const DECLARATION_DOCUMENT_CODES: ReadonlyArray<string> = [
    ...RISK_WARNING_DOCUMENT_CODES,
    COSTS_AND_CHARGES_DOCUMENT_CODE,
];

/**
 * Fail loudly when a code above left the catalogue, or stopped being mandatory.
 *
 * The declarations of section 29 read the MANDATORY firm literature. An item
 * that the catalogue drops, renames or makes optional stops supporting its
 * declaration, and the panel then quietly reverts to a plain confirmation. The
 * projection map must be total (CONTEXT.md), so the parity is a guard and not
 * a comment.
 */
export function assertDeclarationDocumentCodes(): void {
    for (const code of DECLARATION_DOCUMENT_CODES) {
        const item = providedDocumentCatalogueItem(code);
        if (!item) {
            throw new Error(
                `suitability-declaration-support: the provided-document catalogue holds no "${code}". ` +
                    'A declaration of section 29 reads it, so the rename must reach this file.',
            );
        }
        if (!item.mandatory || !item.confirmedByFirm) {
            throw new Error(
                `suitability-declaration-support: "${code}" is no longer a confirmed mandatory item. ` +
                    'A declaration of section 29 reads it as one, so settle the change in both places.',
            );
        }
    }
}

/** The label the catalogue gives a code, or the code when the catalogue lost it. */
function documentLabelOf(code: string): string {
    return providedDocumentCatalogueItem(code)?.label ?? code;
}

export const suitabilityDeclarationSupportSchema = z.object({
    key: suitabilityDeclarationKeySchema,
    /** True when the firm holds data that speaks to this declaration. */
    supported: z.boolean(),
    /**
     * True when the data records the fact that settles the declaration, false
     * when it records that the fact is missing, and null when no data can
     * settle it. A judgement is always null: the system never concludes it.
     */
    satisfied: z.boolean().nullable(),
    /** What the firm holds, in the words the record uses. */
    evidence: z.array(z.string()),
    /** What the data says is still missing. */
    outstanding: z.array(z.string()),
    /**
     * True when the persona of the reader may not read the source. The panel
     * then prints "Withheld from this persona", and it never prints the absence
     * as a fact.
     */
    withheld: z.boolean(),
});
export type SuitabilityDeclarationSupportDto = z.infer<typeof suitabilityDeclarationSupportSchema>;

/** One row of the provided register, in the shape this fold reads. */
export interface DeclarationSupportDocument {
    readonly documentCode: string;
    readonly documentLabel: string;
    readonly provided: 'yes' | 'no' | 'na';
    readonly versionLabel: string | null;
    readonly dateProvided: string | null;
}

export interface DeclarationSupportInput {
    /** The provided-document register of the case (section 4). */
    readonly salesDocuments: ReadonlyArray<DeclarationSupportDocument>;
    /** True when the persona of the reader may not read that register. */
    readonly salesDocumentsWithheld: boolean;
    /** The investment restrictions the fact find holds (section 17). */
    readonly restrictionCategories: ReadonlyArray<string>;
    /**
     * The whole restriction statement of the client, as one block of text.
     *
     * A caller builds it with `foldRestrictionDetail`
     * (`./fact-find-restriction-rules`), which joins each selected category's
     * detail under its label and the free text last. `restrictions_detail`
     * ALONE is not enough: four categories carry their detail in a question of
     * their own (SEP-257 item 16).
     */
    readonly restrictionDetail: string | null;
    /** True when the persona of the reader may not read the fact find. */
    readonly factFindWithheld: boolean;
}

/** Print one register row as the panel names it. */
function describeDocument(row: DeclarationSupportDocument): string {
    const version = row.versionLabel ? ` (${row.versionLabel})` : '';
    if (row.provided === 'na') return `${row.documentLabel}${version}: not applicable`;
    if (row.provided === 'no') return `${row.documentLabel}${version}: not provided`;
    const day = row.dateProvided ? `, ${row.dateProvided}` : '';
    return `${row.documentLabel}${version}: provided${day}`;
}

/**
 * State how far the register settles one document declaration.
 *
 * A `yes` answer settles it. An `na` answer settles it too: an item that does
 * not apply is not an item the firm failed to give. That is the rule
 * `summariseProvidedDocuments` already follows, and the two must agree.
 *
 * A code the register does not carry is OUTSTANDING and not settled. An empty
 * register therefore never settles a declaration.
 */
function supportFromDocuments(
    key: SuitabilityDeclarationKey,
    codes: ReadonlyArray<string>,
    input: DeclarationSupportInput,
): SuitabilityDeclarationSupportDto {
    if (input.salesDocumentsWithheld) {
        return {
            key,
            supported: false,
            satisfied: null,
            evidence: [],
            outstanding: [],
            withheld: true,
        };
    }
    const rows = input.salesDocuments.filter((row) => codes.includes(row.documentCode));
    if (rows.length === 0) {
        return {
            key,
            supported: false,
            satisfied: null,
            evidence: [],
            outstanding: [],
            withheld: false,
        };
    }
    const settled = rows.filter((row) => row.provided === 'yes' || row.provided === 'na');
    const missingCodes = codes.filter((code) => !rows.some((row) => row.documentCode === code));
    const outstanding = [
        ...rows.filter((row) => row.provided === 'no').map(describeDocument),
        // The reader gets the LABEL of the item and never the stored code.
        // `summariseProvidedDocuments` names its missing items the same way, so
        // the two panels of one screen print one vocabulary.
        ...missingCodes.map((code) => `${documentLabelOf(code)}: the register carries no entry`),
    ];
    return {
        key,
        supported: true,
        satisfied: outstanding.length === 0 && settled.length === rows.length,
        evidence: settled.map(describeDocument),
        outstanding,
        withheld: false,
    };
}

/**
 * Build the support block for the six declarations.
 *
 * The order matches `SUITABILITY_DECLARATION_KEYS`, so the panel prints the
 * entries beside the fields of the form without a lookup.
 */
export function buildSuitabilityDeclarationSupport(
    input: DeclarationSupportInput,
): ReadonlyArray<SuitabilityDeclarationSupportDto> {
    const plain = (key: SuitabilityDeclarationKey): SuitabilityDeclarationSupportDto => ({
        key,
        supported: false,
        satisfied: null,
        evidence: [],
        outstanding: [],
        withheld: false,
    });

    // "Investment restrictions considered" is a JUDGEMENT. The platform shows
    // the restrictions the client stated, and it never states that the IM
    // considered them. `satisfied` therefore stays null.
    const restrictionEvidence = [
        ...input.restrictionCategories,
        ...(input.restrictionDetail ? [input.restrictionDetail] : []),
    ];
    const restrictions: SuitabilityDeclarationSupportDto = input.factFindWithheld
        ? {
              key: 'investmentRestrictionsConsidered',
              supported: false,
              satisfied: null,
              evidence: [],
              outstanding: [],
              withheld: true,
          }
        : {
              key: 'investmentRestrictionsConsidered',
              supported: restrictionEvidence.length > 0,
              satisfied: null,
              evidence: restrictionEvidence,
              outstanding: [],
              withheld: false,
          };

    return [
        // The firm holds no record that speaks to these three, so each one
        // stays a plain confirmation by the IM.
        plain('informationCollected'),
        plain('circumstancesAssessed'),
        plain('strategyRecommended'),
        supportFromDocuments('riskWarningsProvided', RISK_WARNING_DOCUMENT_CODES, input),
        restrictions,
        supportFromDocuments('costChargesDisclosed', [COSTS_AND_CHARGES_DOCUMENT_CODE], input),
    ];
}

/** Find one declaration's support entry, or null. */
export function declarationSupportFor(
    entries: ReadonlyArray<SuitabilityDeclarationSupportDto>,
    key: SuitabilityDeclarationKey,
): SuitabilityDeclarationSupportDto | null {
    return entries.find((entry) => entry.key === key) ?? null;
}
